// src/components/RescueIDProfile.jsx

import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Typography,
  Avatar,
  Box,
  Grid,
  Paper,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import FranciscaImage from '../assets/Francisca_Image.jpg';
import ShareLocationButton from './ShareLocationButton';
import { getAccurateLocation } from '../utils/geolocation';

const RootContainer = styled(Container)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  minHeight: '100vh',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(4),
}));

const SectionPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
  transition: 'box-shadow 200ms ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

export default function RescueIDProfile() {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [coords, setCoords] = useState(null);
  const [mapLoading, setMapLoading] = useState(true);

  useEffect(() => {
    getAccurateLocation(3, 20)
      .then(c => setCoords(c))
      .catch(() => setCoords(null))
      .finally(() => setMapLoading(false));
  }, []);

  const handleTabChange = (_e, value) => {
    setTab(value);
    const id = ['personal', 'medica', 'contacto', 'ubicacion'][value];
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const copyCoords = () => {
    if (coords) {
      navigator.clipboard.writeText(`${coords.latitude},${coords.longitude}`);
    }
  };

  return (
    <RootContainer maxWidth="md">
      {/* RUT / Header */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar sx={{ justifyContent: 'center', minHeight: 50 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            RUT: 21.228.036-4
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Avatar & Name */}
      <Box sx={{ textAlign: 'center', mt: 4, mb: 2 }}>
        {FranciscaImage ? (
          <Avatar
            src={FranciscaImage}
            alt="Francisca"
            sx={{
              width: 120,
              height: 120,
              mx: 'auto',
              border: `3px solid ${theme.palette.common.white}`,
              boxShadow: theme.shadows[3],
            }}
          />
        ) : (
          <Skeleton variant="circular" width={120} height={120} />
        )}
        <Typography variant="h4" mt={2}>
          Francisca
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          22 años • Chile, Santiago
        </Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={handleTabChange}
        centered
        indicatorColor="secondary"
        textColor="primary"
        aria-label="Secciones de RescueID"
        sx={{ mb: 3 }}
      >
        {[
          { icon: <PersonIcon />, label: 'Personal' },
          { icon: <LocalHospitalIcon />, label: 'Médica' },
          { icon: <PhoneIcon />, label: 'Contacto' },
          { icon: <LocationOnIcon />, label: 'Ubicación' },
        ].map((item, i) => (
          <Tab
            key={i}
            icon={item.icon}
            label={item.label}
            sx={{
              transition: 'color 200ms',
              '&:hover': { color: theme.palette.secondary.main },
              borderRadius: theme.shape.borderRadius,
            }}
          />
        ))}
      </Tabs>

      {/* Personal */}
      <SectionPaper id="personal">
        <Typography variant="h6" gutterBottom>
          <PersonIcon color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
          Información Personal
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {[
            { icon: <PersonIcon />, text: 'Francisca Danae' },
            { icon: <PersonIcon />, text: 'Jimenez Medina' },
            { icon: <PersonIcon />, text: 'Bombera Activa' },
            { icon: <PersonIcon />, text: 'Femenino' },
            { icon: <LocationOnIcon />, text: 'Chilena' },
          ].map((item, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {React.cloneElement(item.icon, { fontSize: 'small' })}
                <Typography>{item.text}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </SectionPaper>

      {/* Médica (Accordion) */}
      <Accordion defaultExpanded id="medica" sx={{ mb: 3 }} TransitionProps={{ timeout: 300 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <LocalHospitalIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Información Médica</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {[
              ['Tipo de sangre', 'AB I+'],
              ['Enfermedades', 'Ninguna'],
              ['Peso', '75 kg'],
              ['Medicamentos', 'Ninguno'],
              ['Altura', '158 cm'],
              ['Previsión', 'Fonasa B'],
              ['Alergias', 'Sin alergias'],
              ['Operaciones', 'Aumento mamario 2023'],
            ].map(([label, value], i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalHospitalIcon fontSize="small" />
                  <Typography variant="body2">
                    <strong>{label}:</strong> {value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Contacto */}
      <SectionPaper id="contacto">
        <Typography variant="h6" gutterBottom>
          <PhoneIcon color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
          Información de Contacto
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {[
          { name: 'Karen Medina', phone: '56936992313', relation: 'Madre' },
          { name: 'Bastián Márquez', phone: '56979889772', relation: 'Tío' },
        ].map((contact, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: i === 0 ? 1.5 : 0, // espacio entre filas
            }}
          >
            {/* Nombre */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" color="action" />
              <Typography noWrap>{contact.name}</Typography>
            </Box>

            {/* WhatsApp */}
            <Button
              component="a"
              href={`https://api.whatsapp.com/send?phone=${contact.phone}`}
              target="_blank"
              rel="noopener"
              startIcon={<WhatsAppIcon />}
              size="small"
              sx={{ textTransform: 'none', gap: 0.5 }}
            >
              +56 {contact.phone[2]} {contact.phone.slice(3)}
            </Button>

            {/* Relación */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" color="action" />
              <Typography>{contact.relation}</Typography>
            </Box>
          </Box>
        ))}
      </SectionPaper>





      {/* Ubicación (Accordion) */}
      <Accordion id="ubicacion" TransitionProps={{ timeout: 300 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <LocationOnIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Ubicación</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {mapLoading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : coords ? (
            <Box>
              <Box
                component="iframe"
                width="100%"
                height="200"
                src={`https://maps.google.com/maps?q=${coords.latitude},${coords.longitude}&z=15&output=embed`}
                sx={{ border: 0, borderRadius: theme.shape.borderRadius }}
                loading="lazy"
                aria-label="Mapa de ubicación"
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                <IconButton
                  onClick={copyCoords}
                  aria-label="Copiar coordenadas"
                  size="small"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" sx={{ lineHeight: '24px' }}>
                  {coords.latitude.toFixed(5)}, {coords.longitude.toFixed(5)}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography color="error">Sin datos de ubicación</Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <ShareLocationButton phone="56936992313" />
          </Box>
        </AccordionDetails>
      </Accordion>
    </RootContainer>
  );
}
